import {
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Output,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { PlIcon } from '@app/icon-library/icons';
import { BackgroundColor, IconColor, MenuItemComponentInputs } from '@src/app/components/menuItem/menuItem.component';
import { AuthenticationService } from '@app/auth';
import { animate, style, transition, trigger } from '@angular/animations';
import { EntityStatus, EntityType, Level, MenuModel, Module, Step } from '@shared/models/menu.model';
import { ParticipantService } from '@shared/services/participant/participant.service';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { getUrlWithoutParams } from '@app/utils/url';
import { opacityAnimation } from '@app/utils/animations';
import { PlRouter } from '@shared/services/pl-router.service';

export interface EntitySlugs {
  levelSlug: string | null;
  moduleSlug: string | null;
  stepSlugs: string[] | null;
}

export interface StepDisplay extends EntitySlugs {
  link: string | null;
  menuItem: MenuItemComponentInputs;
}

export interface ModuleDisplay extends EntitySlugs {
  menuItem: MenuItemComponentInputs;
  steps?: StepDisplay[];
}

export interface LevelDisplay extends EntitySlugs {
  menuItem: MenuItemComponentInputs;
  modules: ModuleDisplay[];
}

export type EntityDisplay = LevelDisplay | ModuleDisplay | StepDisplay;

interface IconDefinition {
  iconColor: IconColor | null;
  icon: PlIcon;
}

@Component({
  selector: 'pl-menu',
  templateUrl: './menu.component.html',
  animations: [
    trigger('translateX', [
      transition('void => *', [
        style({ transform: 'translateX(-100%)' }),
        animate('250ms', style({ transform: 'translateX(0px)' })),
      ]),
      transition('* => void', [animate('250ms', style({ transform: 'translateX(-100%)' }))]),
    ]),
    opacityAnimation,
  ],
})
export class MenuComponent {
  @Output() public stepMenuItemClicked: EventEmitter<void> = new EventEmitter<void>();

  public levels: Signal<LevelDisplay[]> = computed(() => this.getMenuForDisplay(this.participantService.menu()));

  public modulesForDisplay: WritableSignal<ModuleDisplay[]> = signal([]);
  public stepsForDisplay: WritableSignal<StepDisplay[]> = signal([]);
  public routeSlugs: WritableSignal<EntitySlugs | null> = signal(null);

  protected readonly PlIcon: typeof PlIcon = PlIcon;
  protected readonly BackgroundColor: typeof BackgroundColor = BackgroundColor;

  private destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private authenticationService: AuthenticationService,
    private participantService: ParticipantService,
    private router: Router,
    private plRouter: PlRouter,
  ) {
    this.watchRouteChangeForMenuUpdate();

    effect(
      () => {
        const levels: LevelDisplay[] = this.levels();
        const routeSlugs: EntitySlugs | null = this.routeSlugs();
        this.showStepMenuBasedOnRouteSlugs(levels, routeSlugs);
      },
      { allowSignalWrites: true },
    );
  }

  private getMenuForDisplay(menu: MenuModel | null): LevelDisplay[] {
    if (menu) {
      const levelData: Level[] = menu.menudata[0].children;
      return levelData.map((level: Level) => {
        const iconLeft: IconDefinition = this.getIconFromStatus(level.status, EntityType.level);
        const entitySlug: EntitySlugs | null = this.extractEntitySlugsFromFullUrl(level.fullUrl);
        const menuItem: MenuItemComponentInputs = {
          backgroundColor: BackgroundColor.gray700,
          description: null,
          iconLeft: iconLeft.icon,
          iconLeftColor: iconLeft.iconColor,
          iconRight: null,
          isLocked: level.status === EntityStatus.locked,
          orangeSubtitle: level.status !== EntityStatus.current,
          subtitle: level.name,
          title: level.title,
        };
        return {
          menuItem,
          modules: this.getModulesForLevel(level),
          levelSlug: entitySlug?.levelSlug || null,
          moduleSlug: entitySlug?.moduleSlug || null,
          stepSlugs: entitySlug?.stepSlugs || null,
        };
      });
    }

    return [];
  }

  private getIconFromStatus(status: EntityStatus, type: EntityType): IconDefinition {
    switch (type) {
      case EntityType.level:
      case EntityType.module:
        switch (status) {
          case EntityStatus.locked:
            return {
              iconColor: null,
              icon: PlIcon.lock_inverted_circle,
            };
          case EntityStatus.completed:
            return {
              iconColor: IconColor.green,
              icon: PlIcon.check_inverted_circle,
            };
          case EntityStatus.current:
            return {
              iconColor: IconColor.blue,
              icon: PlIcon.eye_inverted_circle,
            };
        }
        break;
      case EntityType.step:
        switch (status) {
          case EntityStatus.locked:
            return {
              iconColor: IconColor.gray400,
              icon: PlIcon.lock,
            };
          case EntityStatus.completed:
            return {
              iconColor: IconColor.green,
              icon: PlIcon.tick,
            };
          case EntityStatus.current:
            return {
              iconColor: IconColor.blue,
              icon: PlIcon.eye,
            };
        }
        break;
    }

    throw new Error(`Unknown combination of status(${status}) and type(${type})`);
  }

  private getModulesForLevel(level: Level): ModuleDisplay[] {
    const allModules: ModuleDisplay[] = [
      {
        menuItem: {
          backgroundColor: BackgroundColor.black,
          description: null,
          iconLeft: PlIcon.chevron_back,
          iconLeftColor: IconColor.white,
          iconRight: null,
          isLocked: false,
          orangeSubtitle: true,
          subtitle: level.name,
          title: level.title,
        },
        levelSlug: null,
        moduleSlug: null,
        stepSlugs: null,
      },
    ];

    level.children.forEach((module: Module) => {
      const iconLeft: IconDefinition = this.getIconFromStatus(module.status, EntityType.module);
      const entitySlug: EntitySlugs | null = this.extractEntitySlugsFromFullUrl(module.fullUrl);
      allModules.push({
        menuItem: {
          backgroundColor: BackgroundColor.gray700,
          description: null,
          iconLeft: iconLeft.icon,
          iconLeftColor: iconLeft.iconColor,
          iconRight: null,
          isLocked: module.status === EntityStatus.locked,
          orangeSubtitle: false,
          subtitle: module.name,
          title: module.title,
        },
        steps: this.getStepsForModule(level, module),
        levelSlug: entitySlug?.levelSlug || null,
        moduleSlug: entitySlug?.moduleSlug || null,
        stepSlugs: entitySlug?.stepSlugs || null,
      });
    });

    return allModules;
  }

  private getStepsForModule(level: Level, module: Module): StepDisplay[] {
    const allSteps: StepDisplay[] = [
      {
        menuItem: {
          backgroundColor: BackgroundColor.black,
          description: module.title,
          iconLeft: PlIcon.chevron_back,
          iconLeftColor: IconColor.white,
          iconRight: null,
          isLocked: false,
          orangeSubtitle: false,
          subtitle: null,
          title: level.title,
        },
        link: null,
        levelSlug: null,
        moduleSlug: null,
        stepSlugs: null,
      },
    ];

    let previousStepNotHidden: StepDisplay | null = null;
    let stepNumber: number = 1;
    module.children.forEach((step: Step) => {
      if (step.hideStepInMenu) {
        if (previousStepNotHidden !== null) {
          const entitySlugsForHiddenStep: EntitySlugs | null = this.extractEntitySlugsFromFullUrl(step.fullUrl);
          if (previousStepNotHidden.stepSlugs && entitySlugsForHiddenStep?.stepSlugs) {
            previousStepNotHidden.stepSlugs.push(entitySlugsForHiddenStep.stepSlugs[0]);
          } else {
            throw new Error(
              `previousStepNotHidden.stepSlugs or enitySlugsForHiddenStep.stepSlugs is null, this is not normal`,
            );
          }

          if ([EntityStatus.current, EntityStatus.completed].includes(step.status)) {
            const iconLeft: IconDefinition = this.getIconFromStatus(step.status, EntityType.step);
            previousStepNotHidden.menuItem.iconLeft = iconLeft.icon;
            previousStepNotHidden.menuItem.iconLeftColor = iconLeft.iconColor;
          }
        } else {
          console.error(
            `No previousStepNotHidden found, this should not happen, currentLevel=`,
            level,
            ' currentModule=',
            module,
            ` currentStep=`,
            step,
          );
          throw new Error(`First Step in a module is hidden, this is not normal, data is corrupted`);
        }
      } else {
        const iconLeft: IconDefinition = this.getIconFromStatus(step.status, EntityType.step);
        const entitySlug: EntitySlugs | null = this.extractEntitySlugsFromFullUrl(step.fullUrl);
        const stepDisplay: StepDisplay = {
          menuItem: {
            backgroundColor: BackgroundColor.gray700,
            description: `${stepNumber}. ${step.name}`,
            iconLeft: iconLeft.icon,
            iconLeftColor: iconLeft.iconColor,
            iconRight: step.category === 'video' ? PlIcon.video : null,
            isLocked: step.status === EntityStatus.locked,
            orangeSubtitle: false,
            subtitle: null,
            title: null,
          },
          link: step.fullUrl,
          levelSlug: entitySlug?.levelSlug || null,
          moduleSlug: entitySlug?.moduleSlug || null,
          stepSlugs: entitySlug?.stepSlugs || null,
        };

        allSteps.push(stepDisplay);

        previousStepNotHidden = stepDisplay;
        stepNumber += 1;
      }
    });

    return allSteps;
  }

  public logout(): void {
    this.authenticationService.logout();
  }

  public showModules(modules: ModuleDisplay[]): void {
    this.modulesForDisplay.set(modules);
  }

  public showSteps(steps: StepDisplay[] | undefined): void {
    if (steps) {
      this.stepsForDisplay.set(steps);
    } else {
      this.modulesForDisplay.set([]);
    }
  }

  public goToStep(link: string | null, event: MouseEvent): void {
    if (link) {
      this.stepMenuItemClicked.emit();
      this.plRouter.navigate([link], undefined, event);
    } else {
      this.stepsForDisplay.set([]);
    }
  }

  private extractEntitySlugsFromFullUrl(slug: string): EntitySlugs | null {
    if (slug.startsWith('/potentialife-course')) {
      const slugSplitted: string[] = slug.split('/');
      return {
        levelSlug: slugSplitted[2] || null,
        moduleSlug: slugSplitted[3] || null,
        stepSlugs: slugSplitted[4] ? [slugSplitted[4]] : null,
      };
    }

    return null;
  }

  private showStepMenuBasedOnRouteSlugs(levels: LevelDisplay[], entitySlugsInUrl: EntitySlugs | null): void {
    if (entitySlugsInUrl && entitySlugsInUrl.levelSlug !== null) {
      for (const level of levels) {
        if (level.levelSlug === entitySlugsInUrl.levelSlug) {
          this.showModules(level.modules);

          for (const module of level.modules) {
            if (module.moduleSlug === entitySlugsInUrl.moduleSlug) {
              this.showSteps(module.steps);
              return;
            }
          }
        }
      }
    } else {
      this.modulesForDisplay.set([]);
      this.stepsForDisplay.set([]);
    }
  }

  private watchRouteChangeForMenuUpdate(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: RouterEvent) => {
        const urlWithoutParams: string = getUrlWithoutParams(event.url);
        const entitySlugsInUrl: EntitySlugs | null = this.extractEntitySlugsFromFullUrl(urlWithoutParams);
        this.routeSlugs.set(entitySlugsInUrl);
      });
  }
}
